import React from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { NO_IMAGE } from "../../../assets";
import GoogleMapEmbed from "../../../components/general/googleMap";
import { GALLERY_VIDEO } from "../../../constants/constants";
import { BusinessHomeProfile } from "../../../models/businessHomeProfile";
import { AboutSection, AdmissionChecklistSection, AdmissionsSection, BannerSection, CareServicesSection, ComplianceSection, GallerySection, HeaderSection, ProviderAboutSection, RoomsSection, ServiceSection } from "../../../models/manageWebSite";
import { getWebsiteTemplateOption } from "./websiteTemplates";

type Props = {
  templateKey?: string | null;
  businessData: BusinessHomeProfile | null;
  headerData: HeaderSection | null;
  bannerData: BannerSection | null;
  aboutData: AboutSection | null;
  serviceData: ServiceSection | null;
  galleryData: GallerySection | null;
  roomsData?: RoomsSection | null;
  complianceData?: ComplianceSection | null;
  careServicesData?: CareServicesSection | null;
  providerAboutData?: ProviderAboutSection | null;
  admissionsData?: AdmissionsSection | null;
  admissionChecklistData?: AdmissionChecklistSection | null;
  isPreview?: boolean;
  onOpenInquiry?: () => void;
};

const pageShellStyle = {
  maxWidth: "1180px",
  margin: "0 auto",
  padding: "1.25rem 1rem 2rem",
} as const;

const WebsiteTemplateRenderer = ({
  templateKey,
  businessData,
  headerData,
  bannerData,
  aboutData,
  serviceData,
  galleryData,
  careServicesData,
  providerAboutData,
  admissionsData,
  admissionChecklistData,
  isPreview = false,
  onOpenInquiry,
}: Props) => {
  const [lightboxVisible, setLightboxVisible] = React.useState(false);
  const [activeGalleryIndex, setActiveGalleryIndex] = React.useState(0);
  const template = getWebsiteTemplateOption(templateKey);
  const accent = template.accentColor;
  const paletteByTemplate: Record<string, { soft: string; line: string; dark: string; glow: string; }> = {
    "template-1": { soft: "#eef7ff", line: "#bfdbfe", dark: "#0f172a", glow: "#f59e0b" },
    "template-2": { soft: "#ecfdf5", line: "#99f6e4", dark: "#052e2b", glow: "#fb7185" },
    "template-3": { soft: "#f6f0ff", line: "#ddd6fe", dark: "#22113c", glow: "#22d3ee" },
    "template-4": { soft: "#fff5e8", line: "#fdba74", dark: "#431407", glow: "#f97316" },
    "template-5": { soft: "#fff1f7", line: "#f9a8d4", dark: "#4a0624", glow: "#fb7185" },
  };
  const palette = paletteByTemplate[template.key] || paletteByTemplate["template-1"];
  const serviceList = serviceData?.services;
  const activityList = serviceData?.activities;
  const galleryImageList = galleryData?.sectionSignedImg;
  const services: string[] = Array.isArray(serviceList) ? serviceList : [];
  const activities: string[] = Array.isArray(activityList) ? activityList : [];
  const galleryImages: string[] = Array.isArray(galleryImageList) ? galleryImageList : [];
  const heroImage = bannerData?.backgroundSignedImg || bannerData?.sectionSignedImg || aboutData?.sectionSignedImg || NO_IMAGE;
  const aboutImage = aboutData?.sectionSignedImg || bannerData?.sectionSignedImg || heroImage;
  const displayGalleryImages: string[] = galleryImages.length > 0 ? galleryImages : [heroImage, aboutImage].filter(Boolean);
  const phoneNumber = businessData?.businessPhoneNumber || businessData?.providerPhoneNumber || "Contact for details";
  const address = businessData?.formattedAddress || `${businessData?.businessAddressLine1 || ""} ${businessData?.city || ""}`.trim();

  const openLightbox = (index: number) => {
    setActiveGalleryIndex(index);
    setLightboxVisible(true);
  };

  const showPreviousGalleryItem = () => {
    if (displayGalleryImages.length === 0) {
      return;
    }
    setActiveGalleryIndex((prev) => (prev - 1 + displayGalleryImages.length) % displayGalleryImages.length);
  };

  const showNextGalleryItem = () => {
    if (displayGalleryImages.length === 0) {
      return;
    }
    setActiveGalleryIndex((prev) => (prev + 1) % displayGalleryImages.length);
  };

  React.useEffect(() => {
    if (!lightboxVisible) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        showPreviousGalleryItem();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        showNextGalleryItem();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxVisible, displayGalleryImages.length]);

  const renderCtaButton = (label: string, light = false) => {
    if (isPreview || !onOpenInquiry) {
      return null;
    }

    return (
      <button
        type="button"
        onClick={onOpenInquiry}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "0.55rem",
          background: light ? "#ffffff" : accent,
          color: light ? accent : "#ffffff",
          border: light ? "1px solid rgba(15,23,42,0.08)" : `1px solid ${accent}`,
          fontWeight: 700,
          borderRadius: "999px",
          padding: "0.8rem 1.15rem",
          cursor: "pointer",
          fontSize: "0.98rem",
          lineHeight: 1,
          boxShadow: light ? "0 12px 24px rgba(15,23,42,0.08)" : `0 14px 28px ${accent}33`,
        }}
      >
        <i className="pi pi-send" style={{ color: light ? accent : "#ffffff", fontSize: "0.95rem" }} />
        <span style={{ color: light ? accent : "#ffffff" }}>{label}</span>
      </button>
    );
  };

  const renderServiceActivityCompact = (
    background: string,
    borderColor: string,
    toneColor: string,
    title = "Care and daily life"
  ) => {
    const compactServices: string[] = services.length > 0 ? services : ["Personalized care support", "Resident-focused coordination", "Comfortable daily routines"];
    const compactActivities: string[] = activities.length > 0 ? activities : ["Shared meals", "Daily engagement", "Home-based community"];

    return (
      <div
        style={{
          background,
          border: `1px solid ${borderColor}`,
          borderRadius: "24px",
          padding: "1.25rem 1.35rem",
        }}
      >
        <div className="flex flex-wrap justify-content-between align-items-start gap-3">
          <div>
            <div style={{ fontSize: "0.82rem", textTransform: "uppercase", letterSpacing: "0.08em", color: toneColor, fontWeight: 800 }}>
              Services and Activities
            </div>
            <div style={{ fontSize: "1.45rem", fontWeight: 800, color: "#0f172a", marginTop: "0.45rem" }}>{title}</div>
          </div>
          <div style={{ color: "#64748b", fontSize: "0.92rem" }}>
            {compactServices.length} services • {compactActivities.length} activities
          </div>
        </div>
        <div className="grid mt-2">
          <div className="col-12 lg:col-7">
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.55rem" }}>
              {compactServices.map((item) => (
                <span
                  key={`service-${item}`}
                  style={{
                    background: "#ffffff",
                    border: `1px solid ${borderColor}`,
                    color: "#334155",
                    borderRadius: "999px",
                    padding: "0.42rem 0.75rem",
                    fontWeight: 600,
                    fontSize: "0.88rem",
                  }}
                >
                  {item}
                </span>
              ))}
            </div>
          </div>
          <div className="col-12 lg:col-5">
            <div style={{ display: "grid", gap: "0.55rem" }}>
              {compactActivities.slice(0, 4).map((item) => (
                <div
                  key={`activity-${item}`}
                  style={{
                    background: "rgba(255,255,255,0.8)",
                    border: `1px dashed ${borderColor}`,
                    borderRadius: "14px",
                    padding: "0.7rem 0.85rem",
                    color: "#475569",
                    lineHeight: 1.5,
                  }}
                >
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderGalleryGrid = (columns: "dense" | "spread" = "spread") => {
    const images = displayGalleryImages;

    return (
      <div style={{ background: "#ffffff", borderRadius: "24px", border: "1px solid #e2e8f0", padding: "1.2rem 1.3rem" }}>
        <div style={{ maxWidth: "48rem" }}>
          <div>
            <div style={{ fontSize: "0.82rem", textTransform: "uppercase", letterSpacing: "0.08em", color: accent, fontWeight: 800 }}>
              Home Gallery
            </div>
            <div style={{ fontSize: "1.45rem", fontWeight: 800, color: "#0f172a", marginTop: "0.45rem" }}>
              {galleryData?.title || "See the home environment"}
            </div>
          </div>
          {galleryData?.description ? (
            <div style={{ color: "#64748b", lineHeight: 1.7, marginTop: "0.6rem" }}>{galleryData.description}</div>
          ) : null}
        </div>
        <div className="grid mt-2">
          {images.map((image, index) => (
            <div key={`${image}-${index}`} className={columns === "dense" ? "col-6 md:col-4 lg:col-3" : "col-12 md:col-6 lg:col-4"}>
              <div
                onClick={() => openLightbox(index)}
                style={{
                  minHeight: columns === "dense" ? "160px" : "220px",
                  borderRadius: "18px",
                  background: `url(${image || NO_IMAGE}) center/cover no-repeat`,
                  border: "1px solid #e2e8f0",
                  cursor: "pointer",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    background: "linear-gradient(180deg, rgba(15,23,42,0.02) 0%, rgba(15,23,42,0.28) 100%)",
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderContactPanel = (variant: "soft" | "bold" = "soft") => (
    <div
      style={{
        background: variant === "bold" ? `linear-gradient(135deg, ${accent} 0%, #0f172a 100%)` : "#ffffff",
        borderRadius: "24px",
        border: variant === "bold" ? "none" : "1px solid #e2e8f0",
        color: variant === "bold" ? "#ffffff" : "#0f172a",
        padding: "1.3rem 1.4rem",
        height: "100%",
      }}
    >
      <div style={{ fontSize: "0.82rem", textTransform: "uppercase", letterSpacing: "0.08em", color: variant === "bold" ? "rgba(255,255,255,0.8)" : accent, fontWeight: 800 }}>
        Contact
      </div>
      <div style={{ fontSize: "1.5rem", fontWeight: 800, marginTop: "0.45rem" }}>
        Reach out to learn more
      </div>
      <div style={{ marginTop: "0.95rem", lineHeight: 1.8, color: variant === "bold" ? "rgba(255,255,255,0.92)" : "#475569" }}>
        <div>{address}</div>
        <div>{phoneNumber}</div>
      </div>
      <div className="mt-4">
        {isPreview ? (
          <div style={{ color: variant === "bold" ? "rgba(255,255,255,0.86)" : "#64748b", lineHeight: 1.7 }}>
            Preview mode shows the public layout only. Use the live site to submit inquiries.
          </div>
        ) : (
          renderCtaButton("Request Information", variant === "bold")
        )}
      </div>
    </div>
  );

  const renderMapCard = () => (
    <div
      style={{
        overflow: "hidden",
        borderRadius: "24px",
        border: "1px solid #e2e8f0",
        background: "#ffffff",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
      }}
    >
      <div style={{ width: "100%", maxWidth: "100%", display: "flex", justifyContent: "center" }}>
        <GoogleMapEmbed lat={businessData?.latitude} lng={businessData?.longitude} />
      </div>
    </div>
  );

  const renderClosingContactSection = (contactVariant: "soft" | "bold" = "soft") => (
    <div className="grid">
      <div className="col-12 lg:col-7">{renderMapCard()}</div>
      <div className="col-12 lg:col-5">{renderContactPanel(contactVariant)}</div>
    </div>
  );

  const activeGalleryImage = displayGalleryImages[activeGalleryIndex];

  const renderAboutEditorial = () => (
    <div className="grid" style={{ alignItems: "stretch" }}>
      <div className="col-12 lg:col-7">
        <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "28px", padding: "1.35rem 1.45rem", height: "100%" }}>
          <div style={{ fontSize: "0.82rem", textTransform: "uppercase", letterSpacing: "0.08em", color: accent, fontWeight: 800 }}>
            About This Home
          </div>
          <div style={{ fontSize: "1.9rem", fontWeight: 800, color: "#0f172a", marginTop: "0.55rem", lineHeight: 1.2 }}>
            {aboutData?.title || bannerData?.title || "A supportive home environment"}
          </div>
          <div style={{ color: "#475569", lineHeight: 1.85, marginTop: "0.95rem", maxWidth: "66ch" }}>
            {aboutData?.description || bannerData?.description || "Care details will appear here once the website profile is completed."}
          </div>
        </div>
      </div>
      <div className="col-12 lg:col-5">
        <div
          style={{
            height: "100%",
            minHeight: "320px",
            borderRadius: "28px",
            background: `url(${aboutImage}) center/cover no-repeat`,
            border: "1px solid #e2e8f0",
          }}
        />
      </div>
    </div>
  );

  const renderMosaicHero = () => (
    <div style={{ ...pageShellStyle, paddingTop: "1rem" }}>
      <div className="grid" style={{ alignItems: "stretch" }}>
        <div className="col-12 lg:col-7">
          <div
            style={{
              minHeight: "100%",
              background: "#0f172a",
              color: "#ffffff",
              borderRadius: "30px",
              padding: "1.6rem",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: `radial-gradient(circle at top right, ${accent}66 0%, transparent 42%), linear-gradient(135deg, #0f172a 0%, #111827 100%)`,
              }}
            />
            <div style={{ position: "relative", zIndex: 1 }}>
              <div className="mt-4" style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                <div style={{ width: "4.5rem", height: "4.5rem", borderRadius: "22px", background: "#ffffff", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                  <img src={headerData?.logoSignedImg || NO_IMAGE} alt={businessData?.businessName || "Logo"} style={{ width: "64px", height: "64px", objectFit: "contain" }} />
                </div>
                <div>
                  <div style={{ fontSize: "1.95rem", fontWeight: 800, lineHeight: 1.1 }}>{businessData?.businessName}</div>
                  {headerData?.title ? <div style={{ marginTop: "0.35rem", color: "rgba(255,255,255,0.84)" }}>{headerData.title}</div> : null}
                </div>
              </div>
              <div style={{ fontSize: "2.3rem", fontWeight: 800, lineHeight: 1.1, marginTop: "1.35rem", maxWidth: "12ch" }}>
                {bannerData?.title || "Supportive care with a warm home environment"}
              </div>
              <div style={{ color: "rgba(255,255,255,0.9)", lineHeight: 1.85, marginTop: "1rem", maxWidth: "56ch" }}>
                {bannerData?.description || aboutData?.description || "Care details will appear here once the website profile is completed."}
              </div>
              <div className="mt-4">{renderCtaButton("Request Information", true)}</div>
            </div>
          </div>
        </div>
        <div className="col-12 lg:col-5">
          <div className="grid" style={{ height: "100%" }}>
            <div className="col-12">
              <div
                style={{
                  minHeight: "240px",
                  borderRadius: "26px",
                  background: `url(${heroImage}) center/cover no-repeat`,
                  border: "1px solid #e2e8f0",
                }}
              />
            </div>
            <div className="col-6">
              <div
                style={{
                  minHeight: "150px",
                  borderRadius: "22px",
                  background: "#ffffff",
                  border: "1px solid #e2e8f0",
                  padding: "1rem",
                }}
              >
                <div style={{ color: accent, fontWeight: 800, fontSize: "0.82rem", textTransform: "uppercase", letterSpacing: "0.08em" }}>Address</div>
                <div className="mt-2" style={{ color: "#334155", lineHeight: 1.7 }}>{address}</div>
              </div>
            </div>
            <div className="col-6">
              <div
                style={{
                  minHeight: "150px",
                  borderRadius: "22px",
                  background: "#ffffff",
                  border: "1px solid #e2e8f0",
                  padding: "1rem",
                }}
              >
                <div style={{ color: accent, fontWeight: 800, fontSize: "0.82rem", textTransform: "uppercase", letterSpacing: "0.08em" }}>Phone</div>
                <div className="mt-2" style={{ color: "#334155", lineHeight: 1.7 }}>{phoneNumber}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  let templateContent: React.ReactNode;

  if (template.key === "template-2") {
    templateContent = (
      <div style={{ background: "radial-gradient(circle at top left, #d1fae5 0%, #f6fffb 40%, #fff7ed 100%)" }}>
        <div style={{ ...pageShellStyle, paddingTop: "1rem" }}>
          <div style={{ background: `linear-gradient(135deg, ${accent} 0%, ${palette.glow} 36%, #052e2b 100%)`, borderRadius: "34px", padding: "1.8rem", color: "#ffffff", boxShadow: "0 30px 60px rgba(4,120,87,0.18)" }}>
            <div className="grid mt-2" style={{ alignItems: "center" }}>
              <div className="col-12 lg:col-6">
                <div style={{ fontSize: "2.5rem", fontWeight: 800, lineHeight: 1.05 }}>{bannerData?.title || businessData?.businessName}</div>
                <div style={{ marginTop: "1rem", color: "rgba(255,255,255,0.9)", lineHeight: 1.85, maxWidth: "54ch" }}>
                  {bannerData?.description || aboutData?.description || "Care details will appear here once the website profile is completed."}
                </div>
                <div className="mt-4">{renderCtaButton("Request Information", true)}</div>
              </div>
              <div className="col-12 lg:col-6">
                <div
                  style={{
                    minHeight: "320px",
                    borderRadius: "26px",
                    background: `url(${heroImage}) center/cover no-repeat`,
                    border: "1px solid rgba(255,255,255,0.2)",
                  }}
                />
              </div>
            </div>
          </div>
          <div style={{ display: "grid", gap: "1.25rem", marginTop: "1.25rem" }}>
            <div style={{ background: "rgba(255,255,255,0.8)", border: "1px solid #a7f3d0", borderRadius: "32px", padding: "0.7rem", backdropFilter: "blur(12px)" }}>
              {renderAboutEditorial()}
            </div>
            {renderServiceActivityCompact("linear-gradient(135deg, #ffffff 0%, #f0fdfa 100%)", "#99f6e4", accent, "Care, support, and daily engagement")}
            {renderGalleryGrid("dense")}
            {renderClosingContactSection("soft")}
          </div>
        </div>
      </div>
    );
  } else if (template.key === "template-3") {
    templateContent = (
      <div style={{ background: "radial-gradient(circle at top right, #e9d5ff 0%, #faf5ff 44%, #eff6ff 100%)" }}>
        <div style={{ ...pageShellStyle, paddingTop: "1rem" }}>
          <div className="grid" style={{ alignItems: "stretch" }}>
            <div className="col-12 lg:col-4">
              <div style={{ background: "linear-gradient(180deg, #ffffff 0%, #f7f0ff 100%)", border: "1px solid #ddd6fe", borderRadius: "28px", padding: "1.35rem", height: "100%", boxShadow: "0 24px 44px rgba(124,58,237,0.12)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: "1rem", alignItems: "start" }}>
                  <div>
                    <div style={{ fontSize: "1.7rem", fontWeight: 800, color: "#0f172a" }}>{businessData?.businessName}</div>
                    {headerData?.title ? <div className="mt-2" style={{ color: "#64748b", lineHeight: 1.7 }}>{headerData.title}</div> : null}
                  </div>
                  <div style={{ width: "4.1rem", height: "4.1rem", borderRadius: "20px", background: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", border: "1px solid #e2e8f0" }}>
                    <img src={headerData?.logoSignedImg || NO_IMAGE} alt={businessData?.businessName || "Logo"} style={{ width: "58px", height: "58px", objectFit: "contain" }} />
                  </div>
                </div>
                <div style={{ marginTop: "1rem", color: "#475569", lineHeight: 1.8 }}>{address}</div>
                <div style={{ color: "#475569", lineHeight: 1.8 }}>{phoneNumber}</div>
                <div className="mt-4">{renderCtaButton("Contact This Home")}</div>
              </div>
            </div>
            <div className="col-12 lg:col-8">
              <div
                style={{
                  minHeight: "100%",
                  borderRadius: "30px",
                  background: `linear-gradient(135deg, ${accent} 0%, ${palette.glow} 38%, #111827 100%)`,
                  color: "#ffffff",
                  overflow: "hidden",
                  boxShadow: "0 34px 60px rgba(91,33,182,0.2)",
                }}
              >
                <div className="grid" style={{ margin: 0, minHeight: "100%" }}>
                  <div className="col-12 lg:col-7" style={{ padding: "1.7rem" }}>
                    <div style={{ fontSize: "2.4rem", fontWeight: 800, lineHeight: 1.05 }}>{bannerData?.title || "A cleaner way to present your services"}</div>
                    <div style={{ marginTop: "1rem", color: "rgba(255,255,255,0.9)", lineHeight: 1.85 }}>
                      {bannerData?.description || aboutData?.description || "Care details will appear here once the website profile is completed."}
                    </div>
                  </div>
                  <div className="col-12 lg:col-5" style={{ padding: 0 }}>
                    <div style={{ height: "100%", minHeight: "320px", background: `url(${heroImage}) center/cover no-repeat` }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div style={{ display: "grid", gap: "1.25rem", marginTop: "1.25rem" }}>
            {renderServiceActivityCompact("linear-gradient(135deg, #ffffff 0%, #f6f0ff 100%)", "#ddd6fe", accent, "Services presented in a clean, compact layout")}
            {renderAboutEditorial()}
            {renderGalleryGrid("spread")}
            {renderClosingContactSection("bold")}
          </div>
        </div>
      </div>
    );
  } else if (template.key === "template-4") {
    templateContent = (
      <div style={{ background: "radial-gradient(circle at top center, #ffedd5 0%, #fff7ed 42%, #fffbeb 100%)" }}>
        {renderMosaicHero()}
        <div style={pageShellStyle}>
          <div style={{ display: "grid", gap: "1.25rem" }}>
            <div style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.88) 0%, rgba(255,247,237,0.94) 100%)", border: "1px solid #fed7aa", borderRadius: "28px", padding: "0.75rem" }}>
              {renderGalleryGrid("dense")}
            </div>
            <div>{renderServiceActivityCompact("linear-gradient(135deg, #ffffff 0%, #fff1e6 100%)", "#fdba74", accent, "A compact overview of care and activities")}</div>
            {renderAboutEditorial()}
            {renderClosingContactSection("soft")}
          </div>
        </div>
      </div>
    );
  } else if (template.key === "template-5") {
    templateContent = (
      <div style={{ background: "radial-gradient(circle at top left, #ffe4ef 0%, #fff1f7 40%, #fffafc 100%)" }}>
        <div style={{ ...pageShellStyle, paddingTop: "1rem" }}>
          <div className="grid">
            <div className="col-12 lg:col-5">
              <div style={{ background: "linear-gradient(180deg, #ffffff 0%, #fff4f8 100%)", border: "1px solid #f9a8d4", borderRadius: "30px", padding: "1.5rem", height: "100%", boxShadow: "0 26px 48px rgba(214,31,105,0.14)" }}>
                <div className="mt-4" style={{ fontSize: "2.15rem", fontWeight: 800, color: "#0f172a", lineHeight: 1.08 }}>
                  {bannerData?.title || businessData?.businessName}
                </div>
                <div style={{ marginTop: "1rem", color: "#475569", lineHeight: 1.85 }}>
                  {bannerData?.description || aboutData?.description || "Care details will appear here once the website profile is completed."}
                </div>
                <div className="mt-4">{renderCtaButton("Request Information")}</div>
                <div className="mt-4" style={{ paddingTop: "1rem", borderTop: "1px solid #fbcfe8", color: "#475569", lineHeight: 1.8 }}>
                  <div>{address}</div>
                  <div>{phoneNumber}</div>
                </div>
              </div>
            </div>
            <div className="col-12 lg:col-7">
              <div
                style={{
                  minHeight: "100%",
                  borderRadius: "30px",
                  background: `url(${heroImage}) center/cover no-repeat`,
                  border: "1px solid #f9a8d4",
                  position: "relative",
                  overflow: "hidden",
                  boxShadow: "0 30px 58px rgba(190,24,93,0.18)",
                }}
              >
                <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(74,6,36,0.76) 100%)" }} />
                <div style={{ position: "absolute", left: "1.5rem", right: "1.5rem", bottom: "1.5rem", color: "#ffffff" }}>
                  <div style={{ fontSize: "1.6rem", fontWeight: 800 }}>{businessData?.businessName}</div>
                  {headerData?.title ? <div style={{ marginTop: "0.45rem", color: "rgba(255,255,255,0.9)" }}>{headerData.title}</div> : null}
                </div>
              </div>
            </div>
          </div>
          <div style={{ display: "grid", gap: "1.25rem", marginTop: "1.25rem" }}>
            <div className="grid">
              <div className="col-12 lg:col-6">
                <div style={{ background: "#ffffff", border: "1px solid #f9a8d4", borderRadius: "28px", padding: "1.35rem 1.45rem", height: "100%" }}>
                  <div style={{ fontSize: "0.82rem", textTransform: "uppercase", letterSpacing: "0.08em", color: accent, fontWeight: 800 }}>
                    About This Home
                  </div>
                  <div style={{ fontSize: "1.9rem", fontWeight: 800, color: "#0f172a", marginTop: "0.55rem", lineHeight: 1.2 }}>
                    {aboutData?.title || bannerData?.title || "A supportive home environment"}
                  </div>
                  <div style={{ color: "#475569", lineHeight: 1.85, marginTop: "0.95rem", maxWidth: "66ch" }}>
                    {aboutData?.description || bannerData?.description || "Care details will appear here once the website profile is completed."}
                  </div>
                  <div
                    style={{
                      marginTop: "1.05rem",
                      minHeight: "320px",
                      borderRadius: "22px",
                      background: `url(${aboutImage}) center/cover no-repeat`,
                      border: "1px solid #fbcfe8",
                      width: "100%",
                    }}
                  />
                </div>
              </div>
              <div className="col-12 lg:col-6">{renderServiceActivityCompact("linear-gradient(135deg, #ffffff 0%, #fff0f7 100%)", "#f9a8d4", accent, "Professional support without a crowded layout")}</div>
            </div>
            {renderGalleryGrid("dense")}
            {renderClosingContactSection("soft")}
          </div>
        </div>
      </div>
    );
  } else {
    templateContent = (
    <div style={{ background: "radial-gradient(circle at top right, #dbeafe 0%, #f7fbff 42%, #fff7ed 100%)" }}>
      <div
        style={{
          background: `linear-gradient(135deg, ${accent} 0%, ${palette.glow} 42%, #0f172a 100%)`,
          color: "#ffffff",
          borderRadius: "0 0 34px 34px",
          overflow: "hidden",
        }}
      >
        <div style={{ ...pageShellStyle, paddingTop: "1.2rem", paddingBottom: "2.1rem" }}>
          <div className="grid" style={{ alignItems: "center" }}>
            <div className="col-12 lg:col-6">
              <div className="mt-4" style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                <div style={{ width: "4.5rem", height: "4.5rem", borderRadius: "22px", background: "#ffffff", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                  <img src={headerData?.logoSignedImg || NO_IMAGE} alt={businessData?.businessName || "Logo"} style={{ width: "64px", height: "64px", objectFit: "contain" }} />
                </div>
                <div>
                  <div style={{ fontSize: "2rem", fontWeight: 800, lineHeight: 1.08 }}>{businessData?.businessName}</div>
                  {headerData?.title ? <div style={{ marginTop: "0.4rem", color: "rgba(255,255,255,0.86)" }}>{headerData.title}</div> : null}
                </div>
              </div>
              <div style={{ fontSize: "2.35rem", fontWeight: 800, lineHeight: 1.08, marginTop: "1.15rem" }}>
                {bannerData?.title || "Trusted care in a warm home setting"}
              </div>
              <div style={{ marginTop: "1rem", color: "rgba(255,255,255,0.9)", lineHeight: 1.85, maxWidth: "56ch" }}>
                {bannerData?.description || aboutData?.description || "Care details will appear here once the website profile is completed."}
              </div>
              <div className="mt-4">{renderCtaButton("Request Information", true)}</div>
            </div>
            <div className="col-12 lg:col-6">
              <div
                style={{
                  minHeight: "360px",
                  borderRadius: "28px",
                  background: `url(${heroImage}) center/cover no-repeat`,
                  border: "1px solid rgba(255,255,255,0.14)",
                  boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.08)",
                }}
              />
            </div>
          </div>
        </div>
      </div>
      <div style={pageShellStyle}>
        <div style={{ display: "grid", gap: "1.25rem" }}>
          {renderAboutEditorial()}
          <div>{renderServiceActivityCompact("linear-gradient(135deg, #ffffff 0%, #eff6ff 100%)", "#bfdbfe", accent, "Care and engagement at a glance")}</div>
          {renderGalleryGrid("spread")}
          {renderClosingContactSection("soft")}
        </div>
      </div>
    </div>
    );
  }

  return (
    <>
      {templateContent}
      <Dialog
        visible={lightboxVisible}
        onHide={() => setLightboxVisible(false)}
        dismissableMask
        maximized
        focusOnShow={false}
        style={{ background: "#020617" }}
        contentStyle={{ background: "#020617", padding: 0 }}
        headerStyle={{ background: "#020617", color: "#ffffff", borderBottom: "1px solid rgba(255,255,255,0.08)" }}
        header={displayGalleryImages.length > 0 ? `Gallery ${activeGalleryIndex + 1} of ${displayGalleryImages.length}` : "Gallery"}
      >
        {activeGalleryImage ? (
          <div
            className="flex align-items-center justify-content-center"
            style={{ minHeight: "calc(100vh - 8rem)", gap: "1rem", padding: "1rem", background: "#020617" }}
          >
            <Button icon="pi pi-chevron-left" rounded outlined severity="contrast" autoFocus onClick={showPreviousGalleryItem} />
            <div className="flex align-items-center justify-content-center" style={{ flex: 1, height: "100%" }}>
              {activeGalleryImage.indexOf(GALLERY_VIDEO) !== -1 ? (
                <video controls autoPlay style={{ maxWidth: "100%", maxHeight: "80vh", borderRadius: "18px" }}>
                  <source src={activeGalleryImage} type="video/mp4" />
                </video>
              ) : (
                <img
                  src={activeGalleryImage}
                  alt={`Gallery ${activeGalleryIndex + 1}`}
                  style={{ maxWidth: "100%", maxHeight: "80vh", objectFit: "contain", borderRadius: "18px" }}
                />
              )}
            </div>
            <Button icon="pi pi-chevron-right" rounded outlined severity="contrast" onClick={showNextGalleryItem} />
          </div>
        ) : null}
      </Dialog>
    </>
  );
};

export default WebsiteTemplateRenderer;
